import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore, selectIsAdmin } from '../store/authStore';
import { reservationApi, userApi, mcpApi } from '../services/api';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import csLocale from '@fullcalendar/core/locales/cs';
import {
  FiCalendar, 
  FiClock, 
  FiPlus, 
  FiChevronLeft,
  FiChevronRight,
  FiEye, 
  FiEyeOff,
  FiTrash2,
  FiEdit2,
  FiAlertCircle,
  FiX,
  FiCheck,
  FiLoader 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format, parseISO, addDays, addMinutes, differenceInMinutes, startOfDay } from 'date-fns';

const CLEANUP_BUFFER_MINUTES = 30;
const SLOT_MINUTES = 30;

const roundUpToSlot = (date, minutes = SLOT_MINUTES) => {
  const ms = minutes * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

const rangesOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB;

function CalendarPage() {
  const { user, token } = useAuthStore();
  const isAdmin = useAuthStore(selectIsAdmin);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // 'create', 'edit', 'detail'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    duration: 30
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [view, setView] = useState('timeGridWeek'); // 'dayGridMonth', 'timeGridWeek', 'timeGridDay'
  const [calendarTitle, setCalendarTitle] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const calendarRef = useRef(null);
  const visibleRangeRef = useRef(null);

  // Colors for different users
  const userColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
    '#FECA57', '#FF9FF3', '#54A0FF', '#6C5CE7'
  ];

  // Get user color
  const getUserColor = (userId) => {
    if (!userId) return '#999999';
    const index = userId.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % userColors.length;
    return userColors[index];
  };

  // Fetch reservations
  const fetchReservations = useCallback(async (range = visibleRangeRef.current) => {
    try {
      setIsLoading(true);
      setError(null);

      const start = range?.start || startOfDay(new Date());
      const end = range?.end || addDays(start, 90);
      
      const response = await reservationApi.getAll({
        startDate: start.toISOString(),
        endDate: end.toISOString()
      });
      
      const reservations = response.data.reservations || [];
      
      // Transform reservations to FullCalendar events
      const calendarEvents = reservations.map(reservation => {
        const isOwn = Boolean(reservation.isOwn || (user?.id && reservation.userId === user.id));
        const isBlocked = !isAdmin && !isOwn;
        
        return {
          id: reservation.id,
          title: isBlocked ? 'Obsazeno' : reservation.title,
          start: new Date(reservation.startTime),
          end: new Date(reservation.endTime),
          color: isBlocked ? '#e5e7eb' : (reservation.colour || getUserColor(reservation.userId)),
          borderColor: isBlocked ? '#d1d5db' : (reservation.colour || getUserColor(reservation.userId)),
          textColor: isBlocked ? '#6b7280' : '#ffffff',
          extendedProps: {
            description: reservation.description,
            userId: reservation.userId,
            isOwn: isOwn,
            isBlocked: isBlocked,
            isCleanupBuffer: Boolean(reservation.isCleanupBuffer),
            reservation: reservation
          },
          display: 'block',
          editable: false
        };
      });
      
      setEvents(calendarEvents);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Nepodařilo se načíst rezervace. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání rezervací');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isAdmin, token]);

  useEffect(() => {
    fetchReservations();
    
    // Refresh every minute
    const interval = setInterval(() => fetchReservations(), 60000);
    
    return () => clearInterval(interval);
  }, [fetchReservations]);

  const handleDatesSet = useCallback((dateInfo) => {
    const range = {
      start: dateInfo.start,
      end: dateInfo.end
    };

    visibleRangeRef.current = range;
    setCalendarTitle(dateInfo.view.title);
    fetchReservations(range);
  }, [fetchReservations]);

  const handleNavigate = (action) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    calendarApi[action]();
    setCalendarTitle(calendarApi.view.title);
  };

  const handleViewChange = (nextView) => {
    const calendarApi = calendarRef.current?.getApi();
    setView(nextView);
    if (calendarApi) {
      calendarApi.changeView(nextView);
      setCalendarTitle(calendarApi.view.title);
    }
  };

  const isSelectionAllowed = useCallback((selectionInfo) => {
    const start = selectionInfo.start;
    const end = selectionInfo.end;

    if (!isAdmin && start < startOfDay(new Date())) {
      return false;
    }

    if (isAdmin) {
      return true;
    }

    return !events.some((event) => (
      event.extendedProps.isBlocked &&
      rangesOverlap(start, end, event.start, event.end)
    ));
  }, [events, isAdmin]);

  // Filter events based on showBlockedOnly
  const filteredEvents = showBlockedOnly && !isAdmin
    ? events.filter(e => e.extendedProps.isBlocked)
    : events;

  // Handle date select
  const handleDateSelect = useCallback((selectionInfo) => {
    // Check if user is pending
    if (user?.role === 'PENDING') {
      toast.error('Váš účet čeká na schválení administrátorem');
      return;
    }
    
    const startDate = selectionInfo.start;
    const endDate = selectionInfo.end;

    if (!isAdmin && startDate < startOfDay(new Date())) {
      toast.error('Rezervaci nelze vytvořit před aktuálním dnem');
      return;
    }

    if (!isAdmin && !isSelectionAllowed(selectionInfo)) {
      toast.error(`Vybraný čas je obsazený nebo spadá do ${CLEANUP_BUFFER_MINUTES}minutového úklidového bloku`);
      return;
    }
    
    // Validate duration (min 30 min, max 8 hours)
    const duration = differenceInMinutes(endDate, startDate);
    if (duration < 30) {
      toast.error('Minimální doba rezervace je 30 minut');
      return;
    }
    if (duration > 480) {
      toast.error('Maximální doba rezervace je 8 hodin');
      return;
    }
    
    setSelectedDate(startDate);
    setFormData({
      title: '',
      description: '',
      startTime: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
      endTime: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      duration: duration
    });
    setModalType('create');
    setShowModal(true);
  }, [user, isAdmin, isSelectionAllowed]);

  // Handle event click
  const handleEventClick = useCallback((clickInfo) => {
    const event = clickInfo.event;
    
    if (event.extendedProps.isBlocked && !isAdmin) {
      // Show info that this slot is blocked
      toast.info('Tento čas je již obsazen jiným uživatelem');
      return;
    }
    
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.extendedProps.description || '',
      startTime: format(event.start, "yyyy-MM-dd'T'HH:mm:ss"),
      endTime: format(event.end, "yyyy-MM-dd'T'HH:mm:ss"),
      duration: differenceInMinutes(event.end, event.start)
    });
    
    // Admin can see all, users can only see their own
    if (isAdmin || event.extendedProps.isOwn) {
      setModalType('detail');
    } else {
      setModalType('detail');
    }
    setShowModal(true);
  }, [isAdmin]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate duration
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      const duration = differenceInMinutes(end, start);
      
      if (duration < 30 || duration > 480) {
        toast.error('Doba rezervace musí být mezi 30 minutami a 8 hodinami');
        return;
      }

      if (!isAdmin && start < startOfDay(new Date())) {
        toast.error('Rezervaci nelze vytvořit před aktuálním dnem');
        return;
      }
      
      if (modalType === 'create') {
        // Create new reservation
        const response = await reservationApi.create({
          title: formData.title,
          description: formData.description,
          startTime: formData.startTime,
          endTime: formData.endTime
        });
        
        toast.success('Rezervace úspěšně vytvořena');
        
        // Send MCP notification
        try {
          await mcpApi.sendReservationNotification({
            reservationId: response.data.reservation.id,
            notificationType: 'confirmation'
          });
        } catch (mcpError) {
          console.error('MCP notification error:', mcpError);
        }
      } else if (modalType === 'edit' && selectedEvent) {
        // Update reservation
        await reservationApi.update(selectedEvent.id, {
          title: formData.title,
          description: formData.description
        });
        
        toast.success('Rezervace úspěšně upravena');
        
        // Send notification if time changed
        if (new Date(formData.startTime).getTime() !== selectedEvent.start.getTime() ||
            new Date(formData.endTime).getTime() !== selectedEvent.end.getTime()) {
          try {
            await mcpApi.sendReservationNotification({
              reservationId: selectedEvent.id,
              notificationType: 'update'
            });
          } catch (mcpError) {
            console.error('MCP notification error:', mcpError);
          }
        }
      }
      
      // Close modal and refresh
      setShowModal(false);
      fetchReservations();
    } catch (err) {
      console.error('Error saving reservation:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se uložit rezervaci';
      toast.error(message);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedEvent) return;
    
    try {
      // Check if user can delete
      if (!isAdmin && !selectedEvent.extendedProps.isOwn) {
        toast.error('Můžete smazat pouze své vlastní rezervace');
        return;
      }
      
      await reservationApi.delete(selectedEvent.id);
      toast.success('Rezervace zrušena');
      
      // Send notification
      try {
        await mcpApi.sendReservationNotification({
          reservationId: selectedEvent.id,
          notificationType: 'cancel'
        });
      } catch (mcpError) {
        console.error('MCP notification error:', mcpError);
      }
      
      setShowModal(false);
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se zrušit rezervaci';
      toast.error(message);
    }
  };

  // Get available slots for selected date
  const fetchAvailableSlots = async (date) => {
    if (!date) return;
    
    try {
      setLoadingSlots(true);
      const startDate = format(date, 'yyyy-MM-dd');
      const endDate = format(addMinutes(date, 480), 'yyyy-MM-dd'); // 8 hours
      
      const response = await reservationApi.getAvailableSlots({
        startDate,
        endDate,
        duration: formData.duration
      });
      
      setAvailableSlots(response.data.availableSlots || []);
    } catch (err) {
      console.error('Error fetching available slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle duration change
  const handleDurationChange = (newDuration) => {
    if (selectedDate && formData.startTime) {
      const start = new Date(formData.startTime);
      const end = addMinutes(start, parseInt(newDuration));
      
      setFormData(prev => ({
        ...prev,
        duration: parseInt(newDuration),
        endTime: format(end, "yyyy-MM-dd'T'HH:mm:ss")
      }));
      
      fetchAvailableSlots(start);
    }
  };

  // Handle time change
  const handleTimeChange = (startTime, endTime) => {
    setFormData(prev => ({
      ...prev,
      startTime: format(startTime, "yyyy-MM-dd'T'HH:mm:ss"),
      endTime: format(endTime, "yyyy-MM-dd'T'HH:mm:ss")
    }));
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      duration: 30
    });
  };

  // Check if event can be edited
  const canEdit = (event) => {
    return isAdmin || (event && event.extendedProps.isOwn);
  };

  // Check if event can be deleted
  const canDelete = (event) => {
    return isAdmin || (event && event.extendedProps.isOwn);
  };

  // Filter events for admin view
  const adminEvents = isAdmin ? events : events.filter(e => e.extendedProps.isOwn || e.extendedProps.isBlocked);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kalendář rezervací</h1>
          <p className="text-sm text-gray-600 mt-1">
            Zasedací místnost Řipská 15, Praha 3
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
          {/* View toggle */}
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              onClick={() => handleViewChange('dayGridMonth')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                view === 'dayGridMonth' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Měsíc
            </button>
            <button
              onClick={() => handleViewChange('timeGridWeek')}
              className={`px-4 py-2 text-sm font-medium border-l border-r border-gray-200 transition-colors ${
                view === 'timeGridWeek' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Týden
            </button>
            <button
              onClick={() => handleViewChange('timeGridDay')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                view === 'timeGridDay' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Den
            </button>
          </div>

          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              onClick={() => handleNavigate('prev')}
              className="px-3 py-2 text-sm font-medium rounded-l-lg text-gray-600 hover:bg-gray-100 transition-colors"
              title="Předchozí období"
            >
              <FiChevronLeft />
            </button>
            <button
              onClick={() => handleNavigate('today')}
              className="px-4 py-2 text-sm font-medium border-l border-r border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Dnes
            </button>
            <button
              onClick={() => handleNavigate('next')}
              className="px-3 py-2 text-sm font-medium rounded-r-lg text-gray-600 hover:bg-gray-100 transition-colors"
              title="Další období"
            >
              <FiChevronRight />
            </button>
          </div>
          
          {/* Show blocked only toggle (for non-admin users) */}
          {!isAdmin && (
            <button
              onClick={() => setShowBlockedOnly(!showBlockedOnly)}
              className="flex items-center px-4 py-2 text-sm font-medium bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              {showBlockedOnly ? (
                <>
                  <FiEyeOff className="mr-2" />
                  Skrýt moje
                </>
              ) : (
                <>
                  <FiEye className="mr-2" />
                  Zobrazit všechny
                </>
              )}
            </button>
          )}
          
          {/* Create new reservation button */}
          <button
            onClick={() => {
              // Set to current time + 30 min
              const start = roundUpToSlot(addMinutes(new Date(), 30));
              const end = addMinutes(start, 30);
              setSelectedDate(start);
              setFormData({
                title: '',
                description: '',
                startTime: format(start, "yyyy-MM-dd'T'HH:mm:ss"),
                endTime: format(end, "yyyy-MM-dd'T'HH:mm:ss"),
                duration: 30
              });
              setModalType('create');
              setShowModal(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="mr-2" />
            Nová rezervace
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : (
        /* Calendar */
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">
              {calendarTitle}
            </h2>
            {!isAdmin && (
              <p className="text-sm text-gray-500">
                Cizí rezervace a 30 minut po nich jsou zobrazené pouze jako „Obsazeno“.
              </p>
            )}
          </div>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={view}
            locale={csLocale}
            headerToolbar={false}
            events={filteredEvents}
            selectable={true}
            selectAllow={isSelectionAllowed}
            editable={false}
            droppable={false}
            select={handleDateSelect}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            minTime="08:00:00"
            maxTime="19:00:00"
            slotMinTime="08:00:00"
            slotMaxTime="19:00:00"
            allDaySlot={false}
            slotDuration="00:30:00"
            snapDuration="00:15:00"
            eventOverlap={false}
            eventStartEditable={false}
            eventDurationEditable={false}
            selectOverlap={false}
            longPressDelay={250}
            
            // Custom event class for blocked events
            eventClassNames={(arg) => {
              // FullCalendar passes an arg object ({ event, view, ... }), not the
              // event directly. Reading arg.extendedProps was undefined and crashed
              // the whole calendar render (blank page) whenever events were present.
              const classes = [];
              if (arg.event?.extendedProps?.isBlocked) {
                classes.push('fc-event-blocked');
              }
              return classes;
            }}
            
            // Event content
            eventContent={(eventInfo) => (
              <div className="fc-event-main-frame">
                <div className="fc-event-title-container">
                  <div className="fc-event-title fc-sticky">
                    {eventInfo.event.title}
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      )}

      {/* Reservation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {modalType === 'create' ? 'Nová rezervace' : 
                   modalType === 'edit' ? 'Upravit rezervaci' : 
                   'Detail rezervace'}
                </h2>
                {selectedEvent && (
                  <p className="text-sm text-gray-500 mt-1">
                    {format(selectedEvent.start, 'dd.MM.yyyy HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
                  </p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {modalType === 'create' && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                  <p>
                    <strong>Poznámka:</strong> Minimální doba rezervace je 30 minut, maximální 8 hodin.
                  </p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Název *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Název schůzky"
                  required
                  disabled={modalType === 'detail' && !canEdit(selectedEvent)}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Popis
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Popis schůzky (vidí pouze administrátor)"
                  disabled={modalType === 'detail' && !canEdit(selectedEvent)}
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum a čas začátku *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startTime.replace('T', 'T')}
                    onChange={(e) => {
                      const newStart = new Date(e.target.value);
                      const newEnd = addMinutes(newStart, formData.duration);
                      setFormData(prev => ({
                        ...prev,
                        startTime: format(newStart, "yyyy-MM-dd'T'HH:mm:ss"),
                        endTime: format(newEnd, "yyyy-MM-dd'T'HH:mm:ss")
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={modalType === 'detail' && !canEdit(selectedEvent)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum a čas konce *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endTime.replace('T', 'T')}
                    onChange={(e) => {
                      const newEnd = new Date(e.target.value);
                      const duration = differenceInMinutes(newEnd, new Date(formData.startTime));
                      setFormData(prev => ({
                        ...prev,
                        endTime: format(newEnd, "yyyy-MM-dd'T'HH:mm:ss"),
                        duration: Math.max(30, Math.min(480, duration))
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={modalType === 'detail' && !canEdit(selectedEvent)}
                  />
                </div>
              </div>

              {/* Duration slider */}
              {modalType === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trvání: {formData.duration} minut
                  </label>
                  <input
                    type="range"
                    min={30}
                    max={480}
                    step={30}
                    value={formData.duration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>30 min</span>
                    <span>4 hod</span>
                    <span>8 hod</span>
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                {modalType === 'detail' && canDelete(selectedEvent) && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 transition-colors"
                  >
                    <FiTrash2 className="mr-2" />
                    Zrušit rezervaci
                  </button>
                )}
                
                {modalType === 'detail' && canEdit(selectedEvent) && (
                  <button
                    type="button"
                    onClick={() => setModalType('edit')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
                  >
                    <FiEdit2 className="mr-2" />
                    Upravit
                  </button>
                )}
                
                {(modalType === 'create' || modalType === 'edit') && (
                  <>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      Zrušit
                    </button>
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700 transition-colors"
                    >
                      <FiCheck className="mr-2" />
                      {modalType === 'create' ? 'Vytvořit' : 'Uložit změny'}
                    </button>
                  </>
                )}
                
                {modalType === 'detail' && !canEdit(selectedEvent) && !canDelete(selectedEvent) && (
                  <button
                    type="button"
                    onClick={closeModal}
                    className="ml-auto px-4 py-2 bg-gray-600 text-white rounded-lg shadow-sm hover:bg-gray-700 transition-colors"
                  >
                    Zavřít
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Legenda</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-blue-600"></div>
            <span className="text-sm text-gray-600">Vaše rezervace</span>
          </div>
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-green-600"></div>
              <span className="text-sm text-gray-600">Rezervace jiných uživatelů</span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-gray-300"></div>
            <span className="text-sm text-gray-600">Obsazeno</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarPage;
